/**
 * Contractor Home
 *
 * What a contractor needs from the first screen: what still stands between
 * them and being hired, the invitations waiting on a quote, and the jobs they
 * are on. Each source loads on its own, so one failed request costs one panel
 * or one checklist row rather than the page -- except the contractor profile,
 * which every panel depends on. The open-projects count cannot tell: its
 * service answers a failure with an empty list, so it reads as none open.
 */

import type { TFunction } from "i18next";
import { Check, Circle, Hammer, Inbox, Search } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { Skeleton } from "@/components/ui/skeleton";
import { getMyContractorProfile } from "@/features/contractor-profile/services/contractor-profile-api.service";
import {
	browsePublicProjects,
	type ContractorProfile,
	fetchContractorProfile,
	getContractorAssignments,
	getContractorInvitations,
	type ProjectContractorItem,
} from "@/features/marketplace/services/marketplace-api.service";
import {
	fetchMyPayoutAccount,
	type PayoutAccount,
} from "@/features/projects/services/projects-api.service";
import {
	FloatPanel,
	PageHeader,
	Pill,
	StudioButton,
} from "@/features/studio/theme";
import { findTrade } from "@/lib/constants/markets";
import { formatBudgetRange, formatCalendarDate } from "@/lib/format";

/** A panel's own load: its data, or the reason it has none. */
type Panel<T> =
	| { state: "loading" }
	| { state: "ready"; data: T }
	| { state: "error" };

const LOADING = { state: "loading" } as const;

function settle<T>(result: PromiseSettledResult<T>): Panel<T> {
	return result.status === "fulfilled"
		? { state: "ready", data: result.value }
		: { state: "error" };
}

/** "a", "a and b", "a, b and c" -- in the reader's language. */
function joinWithAnd(items: string[], t: TFunction): string {
	if (items.length <= 1) return items[0] ?? "";
	return t("contractorHome.listJoin", {
		head: items.slice(0, -1).join(", "),
		last: items[items.length - 1],
	});
}

/**
 * "Electrician and plumber in Chandigarh and Mohali". Either half is dropped
 * when the contractor has not filled it in, and both together give nothing.
 */
export function describeContractorWork(
	profile: Pick<ContractorProfile, "trades" | "serviceAreas">,
	t: TFunction,
): string {
	const trades = (profile.trades ?? []).map((value) => {
		const trade = findTrade(value);
		return trade ? t(trade.labelKey) : value;
	});
	const areas = profile.serviceAreas ?? [];
	const tradeText = joinWithAnd(trades, t);
	const areaText = joinWithAnd(areas, t);
	if (tradeText && areaText) {
		return t("contractorHome.workIn", {
			trades: tradeText,
			areas: areaText,
		});
	}
	return tradeText || areaText;
}

interface ReadinessStep {
	key: string;
	done: boolean;
	/** Its source failed to load, so neither done nor to-do is known. */
	unchecked?: boolean;
	title: string;
	body: string;
	action?: { label: string; onClick: () => void };
}

function ReadinessPanel({ steps }: { steps: ReadinessStep[] }) {
	const { t } = useTranslation();
	const doneCount = steps.filter((step) => step.done).length;

	return (
		<FloatPanel elevation={2} padding={0}>
			<div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-dashboard-divider px-5 py-4 sm:px-7">
				<h2 className="font-display text-xl font-medium text-dashboard-text">
					{t("contractorHome.readiness.title")}
				</h2>
				<p className="font-body text-sm text-dashboard-muted">
					{t("contractorHome.readiness.progress", {
						done: doneCount,
						total: steps.length,
					})}
				</p>
			</div>
			<ol className="divide-y divide-dashboard-divider">
				{steps.map((step) => (
					<li
						key={step.key}
						className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:gap-5 sm:px-7"
					>
						<span
							aria-hidden="true"
							className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
								step.done
									? "bg-dashboard-accent text-dashboard-on-fill"
									: "border border-dashboard-border text-dashboard-muted-light"
							}`}
						>
							{step.done ? (
								<Check className="h-4 w-4" strokeWidth={2.5} />
							) : (
								<Circle className="h-2 w-2 fill-current" />
							)}
						</span>
						<div className="min-w-0 flex-1">
							<p
								className={`font-body text-[15px] font-semibold ${
									step.done ? "text-dashboard-muted" : "text-dashboard-text"
								}`}
							>
								<span className="sr-only">
									{step.done
										? t("contractorHome.readiness.doneLabel")
										: step.unchecked
											? t("contractorHome.readiness.uncheckedLabel")
											: t("contractorHome.readiness.todoLabel")}
								</span>
								{step.title}
							</p>
							{!step.done && (
								<p className="mt-0.5 max-w-[62ch] font-body text-[13px] leading-relaxed text-dashboard-muted">
									{step.unchecked
										? t("contractorHome.readiness.uncheckedBody")
										: step.body}
								</p>
							)}
						</div>
						{!step.done && !step.unchecked && step.action && (
							<StudioButton
								variant="secondary"
								size="sm"
								onClick={step.action.onClick}
								style={{ alignSelf: "flex-start" }}
							>
								{step.action.label}
							</StudioButton>
						)}
					</li>
				))}
			</ol>
		</FloatPanel>
	);
}

function PanelTitle({
	children,
	aside,
}: {
	children: React.ReactNode;
	aside?: React.ReactNode;
}) {
	return (
		<div className="mb-4 flex items-baseline justify-between gap-3 border-b border-dashboard-divider pb-3">
			<h2 className="font-display text-lg font-medium text-dashboard-text">
				{children}
			</h2>
			{aside}
		</div>
	);
}

function PanelEmpty({
	icon: Icon,
	children,
}: {
	icon: typeof Inbox;
	children: React.ReactNode;
}) {
	return (
		<div className="flex flex-col items-center px-4 py-8 text-center">
			<Icon className="h-7 w-7 text-dashboard-muted-light" aria-hidden="true" />
			<p className="mt-3 max-w-[40ch] font-body text-sm text-dashboard-muted">
				{children}
			</p>
		</div>
	);
}

function InvitationRow({
	invitation,
	onOpen,
}: {
	invitation: ProjectContractorItem;
	onOpen: () => void;
}) {
	const { t, i18n } = useTranslation();
	const project = invitation.project;
	const budget =
		project?.budgetMin || project?.budgetMax
			? formatBudgetRange(project.budgetMin ?? null, project.budgetMax ?? null)
			: null;
	const city = project?.property?.city;

	return (
		<li>
			<button
				type="button"
				onClick={onOpen}
				className="flex w-full items-center gap-4 rounded-xl px-3 py-3 text-left transition-colors hover:bg-dashboard-warm-050 focus-visible:outline focus-visible:outline-2 focus-visible:outline-dashboard-accent"
			>
				<div className="min-w-0 flex-1">
					<p className="truncate font-display text-[15px] font-medium text-dashboard-text">
						{project?.title || t("dashboard.projectInvitation")}
					</p>
					<p className="mt-0.5 font-body text-[13px] text-dashboard-muted">
						{[
							city,
							budget,
							t("contractorHome.invitations.invitedOn", {
								date: formatCalendarDate(
									invitation.createdAt,
									{ day: "numeric", month: "short" },
									i18n.language === "hi" ? "hi-IN" : "en-IN",
								),
							}),
						]
							.filter(Boolean)
							.join(", ")}
					</p>
				</div>
				<span className="shrink-0 font-body text-[13px] font-semibold text-dashboard-accent-dark">
					{t("contractorHome.invitations.quote")}
				</span>
			</button>
		</li>
	);
}

interface StatTileProps {
	label: string;
	value: Panel<number>;
}

/** One number on the contractor's home. A source that failed shows a dash, not a zero. */
function StatTile({ label, value }: StatTileProps) {
	return (
		<FloatPanel elevation={1} padding={20}>
			<p className="font-mono text-[10px] font-semibold uppercase tracking-widest text-dashboard-muted">
				{label}
			</p>
			{value.state === "loading" ? (
				<Skeleton className="mt-2 h-8 w-12 rounded-md" />
			) : (
				<p className="mt-1 font-display text-[32px] font-medium leading-none text-dashboard-text">
					{value.state === "ready" ? value.data : "–"}
				</p>
			)}
		</FloatPanel>
	);
}

function countOf<T>(
	panel: Panel<T[]>,
	keep: (item: T) => boolean,
): Panel<number> {
	return panel.state === "ready"
		? { state: "ready", data: panel.data.filter(keep).length }
		: panel;
}

export function ContractorHome() {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [profile, setProfile] =
		useState<Panel<ContractorProfile | null>>(LOADING);
	const [isPublished, setIsPublished] = useState<Panel<boolean>>(LOADING);
	const [payoutAccount, setPayoutAccount] =
		useState<Panel<PayoutAccount | null>>(LOADING);
	const [invitations, setInvitations] =
		useState<Panel<ProjectContractorItem[]>>(LOADING);
	const [assignments, setAssignments] =
		useState<Panel<ProjectContractorItem[]>>(LOADING);
	const [openProjects, setOpenProjects] = useState<Panel<number>>(LOADING);

	const load = useCallback(async () => {
		setProfile(LOADING);
		setInvitations(LOADING);
		setAssignments(LOADING);
		const [
			profileResult,
			publishedResult,
			payoutResult,
			invitationsResult,
			assignmentsResult,
			openProjectsResult,
		] = await Promise.allSettled([
			fetchContractorProfile(),
			getMyContractorProfile().then((mine) => {
				// The backend creates the page on first read, so null is a
				// failed request, not an unpublished page.
				if (!mine) throw new Error("profile page request failed");
				return mine.profile.isPublished;
			}),
			fetchMyPayoutAccount(),
			getContractorInvitations("invited"),
			getContractorAssignments(),
			browsePublicProjects({ limit: 1 }).then((result) => result.total),
		]);
		setProfile(settle(profileResult));
		setIsPublished(settle(publishedResult));
		setPayoutAccount(settle(payoutResult));
		setInvitations(settle(invitationsResult));
		setAssignments(settle(assignmentsResult));
		setOpenProjects(settle(openProjectsResult));
	}, []);

	useEffect(() => {
		void load();
	}, [load]);

	if (profile.state === "loading") {
		return (
			<div className="space-y-6" data-testid="contractor-home-loading">
				<Skeleton className="h-10 w-72" />
				<Skeleton className="h-48 w-full rounded-2xl" />
				<div className="grid gap-6 lg:grid-cols-[1.5fr_1fr]">
					<Skeleton className="h-56 rounded-2xl" />
					<Skeleton className="h-56 rounded-2xl" />
				</div>
			</div>
		);
	}

	if (profile.state === "error") {
		return (
			<FloatPanel elevation={1} padding={24}>
				<QueryErrorState
					title={t("contractorHome.loadErrorTitle")}
					message={t("contractorHome.loadErrorBody")}
					onRetry={() => void load()}
				/>
			</FloatPanel>
		);
	}

	const contractor = profile.data;
	if (!contractor) {
		return (
			<FloatPanel elevation={1} padding={32}>
				<div className="mx-auto max-w-md text-center">
					<h1 className="font-display text-2xl font-medium text-dashboard-text">
						{t("dashboard.completeProfile")}
					</h1>
					<p className="mt-2 font-body text-sm text-dashboard-muted">
						{t("dashboard.setupContractorProfile")}
					</p>
					<StudioButton
						variant="primary"
						onClick={() => navigate("/onboarding/contractor")}
						style={{ marginTop: 20 }}
					>
						{t("dashboard.completeProfileBtn")}
					</StudioButton>
				</div>
			</FloatPanel>
		);
	}

	const steps: ReadinessStep[] = [
		{
			key: "verified",
			done: contractor.isVerified,
			title: contractor.isVerified
				? t("contractorHome.readiness.verifiedDone")
				: t("contractorHome.readiness.verifiedTodo"),
			body: t("contractorHome.readiness.verifiedBody"),
		},
		{
			key: "published",
			done: isPublished.state === "ready" && isPublished.data,
			unchecked: isPublished.state === "error",
			title:
				isPublished.state === "error"
					? t("contractorHome.readiness.publishedUnchecked")
					: isPublished.state === "ready" && isPublished.data
						? t("contractorHome.readiness.publishedDone")
						: t("contractorHome.readiness.publishedTodo"),
			body: t("contractorHome.readiness.publishedBody"),
			action: {
				label: t("contractorHome.readiness.publishedAction"),
				onClick: () => navigate("/contractor-profile"),
			},
		},
		{
			key: "payout",
			done: payoutAccount.state === "ready" && payoutAccount.data !== null,
			unchecked: payoutAccount.state === "error",
			title:
				payoutAccount.state === "error"
					? t("contractorHome.readiness.payoutUnchecked")
					: payoutAccount.state === "ready" && payoutAccount.data !== null
						? t("contractorHome.readiness.payoutDone")
						: t("contractorHome.readiness.payoutTodo"),
			body: t("contractorHome.readiness.payoutBody"),
			action: {
				label: t("contractorHome.readiness.payoutAction"),
				onClick: () => navigate("/settings#payout"),
			},
		},
	];
	// A step whose source is still loading counts as neither done nor to-do,
	// so the panel waits rather than flashing a checklist that then shrinks.
	const readinessSettled =
		isPublished.state !== "loading" && payoutAccount.state !== "loading";
	const allReady = steps.every((step) => step.done);

	const onSite =
		assignments.state === "ready"
			? assignments.data.filter((a) => a.status === "hired")
			: [];
	const awaitingSignOff =
		assignments.state === "ready"
			? assignments.data.filter((a) => a.status === "completion_requested")
			: [];

	const stats: StatTileProps[] = [
		{
			label: t("contractorHome.stats.invitations"),
			value: countOf(invitations, () => true),
		},
		{
			label: t("contractorHome.stats.onSite"),
			value: countOf(assignments, (a) => a.status === "hired"),
		},
		{
			label: t("contractorHome.stats.awaitingSignOff"),
			value: countOf(assignments, (a) => a.status === "completion_requested"),
		},
		{
			label: t("contractorHome.stats.completed"),
			value: countOf(assignments, (a) => a.status === "completed"),
		},
	];

	return (
		<div className="mx-auto max-w-6xl space-y-6 py-2 lg:py-8">
			<PageHeader
				title={contractor.name}
				description={describeContractorWork(contractor, t) || undefined}
				actions={
					<>
						<StudioButton
							variant="secondary"
							size="sm"
							onClick={() => navigate("/contractor-profile")}
						>
							{t("contractorHome.editProfile")}
						</StudioButton>
						<StudioButton
							variant="primary"
							size="sm"
							icon={Search}
							onClick={() => navigate("/opportunities")}
						>
							{t("contractorHome.findWork")}
						</StudioButton>
					</>
				}
			/>

			{!readinessSettled ? (
				<Skeleton className="h-48 w-full rounded-2xl" />
			) : allReady ? null : (
				<ReadinessPanel steps={steps} />
			)}

			<div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
				{stats.map((stat) => (
					<StatTile key={stat.label} label={stat.label} value={stat.value} />
				))}
			</div>

			<div className="grid items-start gap-6 lg:grid-cols-[1.5fr_1fr]">
				<FloatPanel elevation={1} padding={24}>
					<PanelTitle
						aside={
							invitations.state === "ready" && invitations.data.length > 0 ? (
								<Pill tone="active">{invitations.data.length}</Pill>
							) : null
						}
					>
						{t("contractorHome.invitations.title")}
					</PanelTitle>
					{invitations.state === "loading" ? (
						<div className="space-y-3">
							<Skeleton className="h-14 w-full rounded-xl" />
							<Skeleton className="h-14 w-full rounded-xl" />
						</div>
					) : invitations.state === "error" ? (
						<QueryErrorState
							message={t("contractorHome.invitations.loadError")}
							onRetry={() => void load()}
						/>
					) : invitations.data.length === 0 ? (
						<PanelEmpty icon={Inbox}>
							{contractor.isVerified
								? t("contractorHome.invitations.emptyVerified")
								: t("contractorHome.invitations.emptyUnverified")}
						</PanelEmpty>
					) : (
						<ul className="-mx-3 space-y-1">
							{invitations.data.slice(0, 6).map((invitation) => (
								<InvitationRow
									key={invitation.id}
									invitation={invitation}
									onOpen={() => navigate(`/invitations/${invitation.id}`)}
								/>
							))}
						</ul>
					)}
				</FloatPanel>

				<div className="space-y-6">
					<FloatPanel elevation={1} padding={24}>
						<PanelTitle>{t("contractorHome.onSite.title")}</PanelTitle>
						{assignments.state === "loading" ? (
							<Skeleton className="h-14 w-full rounded-xl" />
						) : assignments.state === "error" ? (
							<QueryErrorState
								message={t("contractorHome.onSite.loadError")}
								onRetry={() => void load()}
							/>
						) : onSite.length === 0 && awaitingSignOff.length === 0 ? (
							<PanelEmpty icon={Hammer}>
								{t("contractorHome.onSite.empty")}
							</PanelEmpty>
						) : (
							<div className="space-y-3">
								<ul className="space-y-2">
									{onSite.slice(0, 3).map((job) => (
										<li
											key={job.id}
											className="truncate font-display text-[15px] text-dashboard-text"
										>
											{job.project?.title || t("common.project")}
										</li>
									))}
								</ul>
								{awaitingSignOff.length > 0 && (
									<p className="font-body text-[13px] text-dashboard-muted">
										{t("contractorHome.onSite.awaitingSignOff", {
											count: awaitingSignOff.length,
										})}
									</p>
								)}
								<StudioButton
									variant="quiet"
									size="sm"
									onClick={() => navigate("/assignments")}
								>
									{t("contractorHome.onSite.viewAll")}
								</StudioButton>
							</div>
						)}
					</FloatPanel>

					<FloatPanel elevation={1} padding={24}>
						<PanelTitle>{t("contractorHome.findWorkPanel.title")}</PanelTitle>
						<p className="font-body text-sm leading-relaxed text-dashboard-muted-dark">
							{openProjects.state === "ready" && openProjects.data > 0
								? t("contractorHome.findWorkPanel.openCount", {
										count: openProjects.data,
									})
								: t("contractorHome.findWorkPanel.noneOpen")}
							{contractor.isVerified
								? null
								: ` ${t("contractorHome.findWorkPanel.quoteAfterVerification")}`}
						</p>
						<StudioButton
							variant="quiet"
							size="sm"
							onClick={() => navigate("/opportunities")}
							style={{ marginTop: 12 }}
						>
							{t("contractorHome.findWorkPanel.browse")}
						</StudioButton>
					</FloatPanel>
				</div>
			</div>
		</div>
	);
}
